/**
 * P0-RETENTION-01 — FAB Orchestrator.
 *
 * Single source of truth for which floating elements are visible at any
 * given moment. Each FAB registers itself; the orchestrator enforces a
 * priority list and a hard cap of 2 visible elements simultaneously.
 *
 * Special rule: when `daily_wow_card` is requested, only it and
 * `retention_fab` are allowed — all other floating elements are hidden
 * temporarily to reduce visual noise during the Daily WOW moment.
 *
 * Components keep mounting their underlying logic — they only hide the
 * floating UI when `useFabVisible` returns false.
 */
import { useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/audit";

export type FabId =
  | "daily_wow_card"
  | "victory_prompt"
  | "retention_fab"
  | "share_fab"
  | "halftime_fab"
  | "focus_fab"
  | "screen_clean_fab";

const PRIORITY: FabId[] = [
  "daily_wow_card",
  "victory_prompt",
  "retention_fab",
  "share_fab",
  "halftime_fab",
  "focus_fab",
  "screen_clean_fab",
];

const MAX_VISIBLE = 2;

function isMobilePortrait(): boolean {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 640 && window.innerHeight >= window.innerWidth;
}

const requested = new Set<FabId>();
let visible = new Set<FabId>();
let version = 0;
const listeners = new Set<() => void>();

function recompute() {
  const sorted = PRIORITY.filter((id) => requested.has(id));
  let allowed: FabId[];
  if (requested.has("daily_wow_card")) {
    allowed = sorted.filter(
      (id) => id === "daily_wow_card" || id === "retention_fab",
    );
  } else {
    // P0 — On mobile portrait only ONE FAB may appear at a time
    // (Mobile UX Final). Larger viewports keep the historical cap of 2.
    const cap = isMobilePortrait() ? 1 : MAX_VISIBLE;
    allowed = sorted.slice(0, cap);
  }
  const next = new Set<FabId>(allowed);
  for (const id of visible) {
    if (!next.has(id)) {
      void trackEvent("fab_orchestrator_hidden", { id });
    }
  }
  visible = next;
  version += 1;
  listeners.forEach((l) => l());
}

export function registerFab(id: FabId): () => void {
  requested.add(id);
  recompute();
  return () => {
    requested.delete(id);
    recompute();
  };
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
const getSnap = () => version;

export function useFabVisible(id: FabId): boolean {
  useSyncExternalStore(subscribe, getSnap, getSnap);
  return visible.has(id);
}

export function useDailyWowActive(): boolean {
  useSyncExternalStore(subscribe, getSnap, getSnap);
  return requested.has("daily_wow_card");
}

export function isDailyWowActive(): boolean {
  return requested.has("daily_wow_card");
}
